import * as React from "react";
import { type Without } from "../../../types/internal/utility";
import type BaseDataSource from "../_Bases/BaseDataSource";
import { determineDataSource } from "./DataSource";

/*
 * In order to use the withDataSource HOC with Redux, you must ensure
 * that the InjectedDataSourceProps is at the same level as the ConnectedProps,
 * it cannot be embedded inside ConnectedProps.
 *
 * See AssociateProductQuantity.tsx for a live example.
 *
 * Bad:
 * interface InstanceProps extends InjectedDataSourceProps {
 *     foo: boolean;
 * }
 *
 * const connector = connect();
 *
 * interface Props extends ConnectedProps<typeof connector>, InstanceProps {
 *     dispatch: ThunkDispatch<any, any, any>;
 * }
 *
 * Good:
 * interface InstanceProps {
 *     foo: boolean;
 * }
 *
 * const connector = connect();
 *
 * interface Props extends ConnectedProps<typeof connector>, InstanceProps, InjectedDataSourceProps {
 *     dispatch: ThunkDispatch<any, any, any>;
 * }
 */

export interface InjectedDataSourceProps {
    data: Promise<BaseDataSource>;
}

interface WithDataSourceState {
    data: Promise<BaseDataSource>;
}

/**
 * Provide access to the data source for all information from Shopfront
 */
export function withDataSource<C extends React.ComponentType<React.ComponentProps<C> & InjectedDataSourceProps>,
    WrappedProps = React.JSX.LibraryManagedAttributes<C, Without<React.ComponentProps<C>, InjectedDataSourceProps>>>(
    WrappedComponent: C
): React.ComponentType<WrappedProps> {
    return class extends React.Component<WrappedProps, WithDataSourceState> {
        public displayName = "WithDataSource";

        constructor(props: Readonly<WrappedProps>) {
            super(props);

            this.state = {
                data: determineDataSource(),
            };
        }

        public render(): React.ReactNode {
            return (
                <WrappedComponent
                    {...this.props as any}
                    data={this.state.data}
                />
            );
        }
    };
}
